import type { GeoJsonFeature, GeoJsonFeatureCollection } from './geoJson';
import type { MapPOI } from './interfaces';
import { thousandSeparated } from '../util/formatting';

const ZOOM_BREAKPOINT = 14;

export interface MapImage {
    id: string;
    url: string;
    offset?: [number, number];
    options?: Record<string, unknown>;
}

export interface MapLayer {
    id: string;
    options: {
        source: string;
        [key: string]: unknown;
    };
}

export interface MapSource {
    id: string;
    options: {
        type: string;
        data?: GeoJsonFeatureCollection;
        cluster?: boolean;
        clusterMaxZoom?: number;
        clusterRadius?: number;
        tiles?: string[];
    };
}

export const convertToGeoJson = (
    pois: MapPOI[],
    hoverId?: string,
    hoverIcon?: string,
    hoverPriceIcon?: string,
): GeoJsonFeatureCollection => ({
    type: 'FeatureCollection',
    features: pois.map(
        (poi): GeoJsonFeature => ({
            type: 'Feature',
            id: poi.id,
            properties: {
                id: poi.id,
                name: poi.type ? `${poi.name} - ${poi.type}` : poi.name,
                link: poi.link,
                icon: hoverId && poi.id === hoverId ? (poi.price ? hoverPriceIcon : hoverIcon) : undefined,
                count: poi.count,
                price: poi.price ? `${thousandSeparated(poi.price)} kr` : undefined,
            },
            geometry: {
                type: 'Point',
                coordinates: poi.coordinates,
            },
        }),
    ),
});

export const createGeoJsonSource = (id: string, pois: MapPOI[], cluster = false, clusterRadius = 40): MapSource => ({
    id,
    options: {
        type: 'geojson',
        data: convertToGeoJson(pois),
        cluster,
        clusterMaxZoom: 15,
        clusterRadius,
    },
});

const selected = require('./icons/selected.png');
const selectedPriceOption = require('./icons/selected-price-option.png');
const primary = require('./icons/primary.png');
const primaryHover = require('./icons/primary-hover.png');
const primaryPrice = require('./icons/primary-price.png');
const primaryPriceHover = require('./icons/primary-price-hover.png');

const lookupImageSrcFor = (image): string => {
    if (typeof image === 'string') {
        return image;
    }
    // An object with src, width and height is returned from require on newer Next.js versions
    return image.default.src;
};

export const SELECTED_PIN_ICON: MapImage = {
    url: lookupImageSrcFor(selected),
    id: 'pin-icon-selected',
    offset: [0, -28],
};

export const SELECTED_PRICE_PIN_ICON: MapImage = {
    url: lookupImageSrcFor(selectedPriceOption),
    id: 'pin-icon-selected-price',
    offset: [0, -12],
    options: {
        stretchX: [
            [28, 29],
            [133, 134],
        ],
        stretchY: [
            [19, 20],
            [56, 57],
        ],
        content: [28, 19, 134, 57],
        pixelRatio: 2,
    },
};

export const PRIMARY_PIN_ICON: MapImage = {
    url: lookupImageSrcFor(primary),
    id: 'pin-icon-primary',
    offset: [0, -17],
};

export const PRIMARY_HOVER_PIN_ICON: MapImage = {
    url: lookupImageSrcFor(primaryHover),
    id: 'pin-icon-primary-hover',
    offset: [0, -17],
};

export const PRIMARY_PRICE_PIN_ICON: MapImage = {
    url: lookupImageSrcFor(primaryPrice),
    id: 'pin-icon-primary-price',
    offset: [0, -12],
    options: {
        stretchX: [
            [28, 29],
            [133, 134],
        ],
        stretchY: [
            [19, 20],
            [56, 57],
        ],
        content: [28, 19, 134, 57],
        pixelRatio: 2,
    },
};

export const PRIMARY_PRICE_HOVER_PIN_ICON: MapImage = {
    url: lookupImageSrcFor(primaryPriceHover),
    id: 'pin-icon-primary-price-hover',
    offset: [0, -12],
    options: {
        stretchX: [
            [28, 29],
            [133, 134],
        ],
        stretchY: [
            [19, 20],
            [56, 57],
        ],
        content: [28, 19, 134, 57],
        pixelRatio: 2,
    },
};

export const SELECTED_POINT_LAYER: MapLayer = {
    id: 'selected-points',
    options: {
        type: 'symbol',
        source: 'selected-data',
        filter: ['all', ['!', ['has', 'point_count']], ['!', ['has', 'price']]],
        layout: {
            'icon-image': SELECTED_PIN_ICON.id,
            'icon-size': 0.5,
            'icon-ignore-placement': true,
            'icon-allow-overlap': true,
            'icon-offset': SELECTED_PIN_ICON.offset,
            'symbol-z-order': 'source',
        },
    },
};

export const SELECTED_PRICE_POINT_LAYER: MapLayer = {
    id: 'selected-price-points',
    options: {
        type: 'symbol',
        source: 'selected-data',
        filter: ['all', ['!', ['has', 'point_count']], ['has', 'price']],
        layout: {
            'text-field': ['get', 'price'],
            'text-font': ['Open Sans Bold'],
            'text-size': 12,
            'icon-text-fit': 'both',
            'icon-image': ['string', ['get', 'icon'], SELECTED_PRICE_PIN_ICON.id],
            'icon-ignore-placement': true,
            'icon-allow-overlap': true,
            'text-allow-overlap': true,
            'symbol-z-order': 'source',
        },
        paint: {
            'text-color': '#fff',
        },
    },
};

export const PRIMARY_CLUSTER_LOW_ZOOM_LAYER: MapLayer = {
    id: 'primary-clusters-low-zoom',
    options: {
        type: 'circle',
        source: 'primary-low-zoom-data',
        maxzoom: ZOOM_BREAKPOINT,
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': ['case', ['boolean', ['feature-state', 'hover'], false], 'rgb(0, 79, 201)', 'rgb(0, 99, 251)'],
            'circle-radius': ['step', ['get', 'point_count'], 15, 50, 20, 200, 25, 500, 30],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
        },
    },
};

export const PRIMARY_CLUSTER_HIGH_ZOOM_LAYER: MapLayer = {
    id: 'primary-clusters-high-zoom',
    options: {
        type: 'circle',
        source: 'primary-high-zoom-data',
        minzoom: ZOOM_BREAKPOINT,
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': ['case', ['boolean', ['feature-state', 'hover'], false], 'rgb(0, 79, 201)', 'rgb(0, 99, 251)'],
            'circle-radius': ['step', ['get', 'point_count'], 12, 50, 15, 200, 20],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
        },
    },
};

export const PRIMARY_CLUSTER_SHADOW_LOW_ZOOM_LAYER: MapLayer = {
    id: 'primary-clusters-shadow-low-zoom',
    options: {
        type: 'circle',
        source: 'primary-low-zoom-data',
        maxzoom: ZOOM_BREAKPOINT,
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': '#000',
            'circle-radius': ['step', ['get', 'point_count'], 18, 50, 23, 200, 28, 500, 33],
            'circle-blur': 0.4,
            'circle-translate': [0, 3],
        },
    },
};

export const PRIMARY_CLUSTER_SHADOW_HIGH_ZOOM_LAYER: MapLayer = {
    id: 'primary-clusters-shadow-high-zoom',
    options: {
        type: 'circle',
        source: 'primary-high-zoom-data',
        minzoom: ZOOM_BREAKPOINT,
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': '#000',
            'circle-radius': ['step', ['get', 'point_count'], 15, 50, 28, 200, 23],
            'circle-blur': 0.4,
            'circle-translate': [0, 3],
        },
    },
};

export const PRIMARY_CLUSTER_COUNT_LOW_ZOOM_LAYER: MapLayer = {
    id: 'primary-clusters-count-low-zoom',
    options: {
        type: 'symbol',
        source: 'primary-low-zoom-data',
        maxzoom: ZOOM_BREAKPOINT,
        filter: ['has', 'point_count'],
        layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Open Sans Bold'],
            'text-size': 12,
            'text-allow-overlap': true,
        },
        paint: {
            'text-color': '#fff',
        },
    },
};

export const PRIMARY_CLUSTER_COUNT_HIGH_ZOOM_LAYER: MapLayer = {
    id: 'primary-clusters-count-high-zoom',
    options: {
        type: 'symbol',
        source: 'primary-high-zoom-data',
        minzoom: ZOOM_BREAKPOINT,
        filter: ['has', 'point_count'],
        layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Open Sans Bold'],
            'text-size': 12,
            'text-allow-overlap': true,
        },
        paint: {
            'text-color': '#fff',
        },
    },
};

export const PRIMARY_POINT_LOW_ZOOM_LAYER: MapLayer = {
    id: 'primary-points-low-zoom',
    options: {
        type: 'symbol',
        source: 'primary-low-zoom-data',
        maxzoom: ZOOM_BREAKPOINT,
        filter: ['all', ['!', ['has', 'point_count']], ['!', ['has', 'price']]],
        layout: {
            'icon-image': ['string', ['get', 'icon'], PRIMARY_PIN_ICON.id],
            'icon-size': 0.45,
            'icon-ignore-placement': true,
            'icon-allow-overlap': true,
            'icon-offset': PRIMARY_PIN_ICON.offset,
            'symbol-z-order': 'source',
        },
    },
};

export const PRIMARY_POINT_HIGH_ZOOM_LAYER: MapLayer = {
    id: 'primary-points-high-zoom',
    options: {
        type: 'symbol',
        source: 'primary-high-zoom-data',
        minzoom: ZOOM_BREAKPOINT,
        filter: ['all', ['!', ['has', 'point_count']], ['!', ['has', 'price']]],
        layout: {
            'icon-image': ['string', ['get', 'icon'], PRIMARY_PIN_ICON.id],
            'icon-size': 0.45,
            'icon-ignore-placement': true,
            'icon-allow-overlap': true,
            'icon-offset': PRIMARY_PIN_ICON.offset,
            'symbol-z-order': 'source',
        },
    },
};

export const PRIMARY_PRICE_POINT_LOW_ZOOM_LAYER: MapLayer = {
    id: 'primary-price-points-low-zoom',
    options: {
        type: 'symbol',
        source: 'primary-low-zoom-data',
        maxzoom: ZOOM_BREAKPOINT,
        filter: ['all', ['!', ['has', 'point_count']], ['has', 'price']],
        layout: {
            'text-field': ['get', 'price'],
            'text-font': ['Open Sans Bold'],
            'text-size': 12,
            'icon-text-fit': 'both',
            'icon-image': ['string', ['get', 'icon'], PRIMARY_PRICE_PIN_ICON.id],
            'icon-ignore-placement': true,
            'icon-allow-overlap': true,
            'text-allow-overlap': true,
            'symbol-z-order': 'source',
        },
        paint: {
            'text-color': '#fff',
        },
    },
};

export const PRIMARY_PRICE_POINT_HIGH_ZOOM_LAYER: MapLayer = {
    id: 'primary-price-points-high-zoom',
    options: {
        type: 'symbol',
        source: 'primary-high-zoom-data',
        minzoom: ZOOM_BREAKPOINT,
        filter: ['all', ['!', ['has', 'point_count']], ['has', 'price']],
        layout: {
            'text-field': ['get', 'price'],
            'text-font': ['Open Sans Bold'],
            'text-size': 12,
            'icon-text-fit': 'both',
            'icon-image': ['string', ['get', 'icon'], PRIMARY_PRICE_PIN_ICON.id],
            'icon-ignore-placement': true,
            'icon-allow-overlap': true,
            'text-allow-overlap': true,
            'symbol-z-order': 'source',
        },
        paint: {
            'text-color': '#fff',
        },
    },
};

export const SERVER_SIDE_CLUSTER_LAYER: MapLayer = {
    id: 'server-side-clusters',
    options: {
        type: 'circle',
        source: 'server-side-clusters',
        paint: {
            'circle-color': ['case', ['boolean', ['feature-state', 'hover'], false], 'rgb(0, 79, 201)', 'rgb(0, 99, 251)'],
            'circle-radius': ['step', ['get', 'count'], 15, 100, 20, 1000, 22, 5000, 25],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff',
        },
    },
};
export const SERVER_SIDE_CLUSTER_SHADOW_LAYER: MapLayer = {
    id: 'server-side-clusters-shadow',
    options: {
        type: 'circle',
        source: 'server-side-clusters',
        paint: {
            'circle-color': '#000',
            'circle-radius': ['step', ['get', 'count'], 18, 100, 23, 1000, 25, 5000, 28],
            'circle-blur': 0.4,
            'circle-translate': [0, 3],
        },
    },
};
export const SERVER_SIDE_CLUSTER_COUNT_LAYER: MapLayer = {
    id: 'server-side-clusters-count',
    options: {
        type: 'symbol',
        source: 'server-side-clusters',
        layout: {
            'text-field': '{name}',
            'text-font': ['Open Sans Bold'],
            'text-size': 12,
        },
        paint: {
            'text-color': '#fff',
        },
    },
};
